import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageRange from '../../common/components/PageRange';
import Pagination from '../../common/components/Pagination';
import { openInNewTabGeneric, useQuery } from '../../common/helpers';
import { useAppSelector } from '../../common/hooks';
import { ORDERS_PAGE_SIZE } from '../../constants';
import { IOrdersContainer } from '../../models/Orders/IOrdersContainer';
import { RootState } from '../../redux/store';
import { getOrdersPaged, handleErrorResponse, printOrders } from '../../services';
import OrdersDonationsCard from './components/OrdersCard';
import styles from './OrdersPage.module.css';
import { env } from '../../env';
import LoadingSpinner from '../../common/components/LoadingSpinner';

const OrdersDonationsPage = () => {
  const pageSize = ORDERS_PAGE_SIZE;
  const [ordersData, setOrdersData] = useState<IOrdersContainer>({} as IOrdersContainer);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();
  const pageNumber = useQuery().get('pageNumber');

  const profileContainer = useAppSelector(
    (state: RootState) => state.profileReducer.profileContainer
  );

  const setPage = () => {
    if (pageNumber) {
      setCurrentPage(+pageNumber);
    } else {
      navigate('/orders?pageNumber=1', { replace: true });
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getOrdersPaged(profileContainer?.profileModel?.masterCustomerId, pageSize, currentPage)
      .then((response: any) => {
        setOrdersData(response.data);
      })
      .catch((_error: any) => {
        handleErrorResponse(_error);
      })
      .finally(() => {
        setIsLoading(false);
        setPage();
      });
  }, [currentPage]);

  const pageStatus = () => {
    const firstItem = (currentPage - 1) * pageSize + 1;
    const max = currentPage * pageSize;
    const lastItem = ordersData?.totalCount < max ? ordersData?.totalCount : max;
    if (lastItem === firstItem) {
      return firstItem.toString();
    } else {
      return firstItem + ' - ' + lastItem;
    }
  };

  const changeCurrentPage = (pageNumber: number) => {
    navigate(`/orders?pageNumber=${pageNumber}`);
    setCurrentPage(pageNumber);
  };

  const handleOrderPrint = (orderNumber: string) => {
    if (orderNumber && profileContainer?.profileModel?.masterCustomerId) {
      const printMethod = printOrders(
        profileContainer?.profileModel?.masterCustomerId,
        orderNumber
      );
      if (env.REACT_APP_DASHBOARD_API_URL) {
        openInNewTabGeneric(env.REACT_APP_DASHBOARD_API_URL + printMethod);
      } else {
        alert('Print method is undefined!');
      }
    }
  };

  return (
    <div className={styles.container}>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className={styles.italicNote}>
            Note: These are order invoices, not payment receipts. Please check your profile email
            for your payment receipt.
          </div>
          {ordersData?.orders?.length === 0 ? (
            <p className={styles.textCenter}>You have no history of orders and donations.</p>
          ) : (
            <>
              <div className={styles.navigation}>
                <div> </div>
                <div className={styles.numbering}>
                  <PageRange currentPageRange={pageStatus()} totalItems={ordersData?.totalCount} />
                </div>
                <div className={styles.pagination}>
                  <Pagination
                    itemsPerPage={pageSize}
                    totalItems={ordersData?.totalCount}
                    totalPages={ordersData?.totalPages}
                    changePage={changeCurrentPage}
                    currentPage={currentPage}
                  />
                </div>
              </div>
              <div>
                {ordersData?.orders?.map(order => {
                  return order?.orderLines?.map(orderLine => {
                    return (
                      <OrdersDonationsCard
                        orderLine={orderLine}
                        orderNumber={order?.orderNumber}
                        handleOrderPrint={handleOrderPrint}
                      />
                    );
                  });
                })}
              </div>
              <div className={styles.paginationMobile}>
                <Pagination
                  itemsPerPage={pageSize}
                  totalItems={ordersData?.totalCount}
                  totalPages={ordersData?.totalPages}
                  changePage={changeCurrentPage}
                  currentPage={currentPage}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default OrdersDonationsPage;
